import React, { memo, useMemo } from 'react';
import type { DayColumnDescriptor, MonthDescriptor } from '../utils/types';
import { getWeekendCellBackground } from '../utils/getWeekendCellBackground';
import { getMonthSectionWidthPx } from '../utils/getVisibleDayColumns';
import {
  DAY_WIDTH_PX,
  DAY_CELL_HORIZONTAL_PADDING_PX
} from '../utils/monthlyLayoutConstants';
import styles from '../MonthlyRoadmap.module.scss';

interface MonthlyGridBackgroundProps {
  months: MonthDescriptor[];
  dayColumns: DayColumnDescriptor[];
  expandedMonths: string[];
  isCompact: boolean;
}

export const MonthlyGridBackground = memo(
  ({
    months,
    dayColumns,
    expandedMonths,
    isCompact
  }: MonthlyGridBackgroundProps) => {
    const timelineWidthPx = useMemo(
      () =>
        months.reduce((total, month) => {
          const isExpanded = expandedMonths.includes(month.key);
          return (
            total +
            getMonthSectionWidthPx(
              month.daysInMonth,
              isExpanded,
              isCompact,
              DAY_WIDTH_PX
            )
          );
        }, 0),
      [months, expandedMonths, isCompact]
    );

    return (
      <div className={styles.monthlyGridBackground} aria-hidden>
        <div
          className={styles.monthlyGridBackgroundColumns}
          style={{
            left: `${DAY_CELL_HORIZONTAL_PADDING_PX}px`,
            width: `${timelineWidthPx}px`
          }}
        >
          {dayColumns.map((column, index) => {
            const month = months[column.monthIndex];
            const isMonthEnd =
              index === dayColumns.length - 1 ||
              dayColumns[index + 1].monthKey !== column.monthKey;

            const nextSameMonth = isMonthEnd ? null : dayColumns[index + 1];
            const colSpan = nextSameMonth
              ? nextSameMonth.day - column.day
              : month.daysInMonth - column.day + 1;

            const isExpanded = expandedMonths.includes(month.key);

            const weekendBackground = getWeekendCellBackground(
              column.day,
              colSpan,
              month,
              column.monthIndex,
              months,
              isExpanded,
              isCompact
            );

            return (
              <div
                key={column.key}
                className={`${styles.monthlyGridBackgroundCell} ${
                  isMonthEnd ? styles.monthlyGridMonthEndCell : ''
                }`}
                style={
                  weekendBackground
                    ? { background: weekendBackground }
                    : undefined
                }
              />
            );
          })}
        </div>
      </div>
    );
  }
);

MonthlyGridBackground.displayName = 'MonthlyGridBackground';
